export const permissionGroups = [
    {
        groupKey: 'rma',
        parentPermission: 'RMA',
        childGroups: [
            {
                childGroupKey: 'rmaRole',
                childPermissions: ['RMA:Read-Only', 'RMA:Operations', 'RMA:Sales', 'RMA:Manager'],
                selectionMode: 'exclusive', // exclusive means that only one child can be selected at a time in this group
                defaultChild: 'RMA:Read-Only',
                requireSelection: true,
            },
        ],
    },
    // More permission groups can be added here as needed to apply custom constraints on child permissions.
];

function splitPermissionValue(permissionValue) {
    if (!permissionValue.includes(':')) {
        return { parent: permissionValue, child: '', isChild: false };
    }

    const [parent, ...childParts] = permissionValue.split(':');
    const child = childParts.join(':');
    return {
        parent,
        child,
        isChild: Boolean(parent && child),
    };
}

function getAllChildPermissions(permissionGroup) {
    return (permissionGroup?.childGroups || []).flatMap((childGroup) => childGroup.childPermissions || []);
}

function findChildGroup(parentPermission, permissionValue, permissionMetadata = defaultPermissionMetadata) {
    const permissionGroup = permissionMetadata.parentGroupByPermission.get(parentPermission);
    if (!permissionGroup) return null;
    return (permissionGroup.childGroups || []).find((childGroup) => (
        (childGroup.childPermissions || []).includes(permissionValue)
    )) || null;
}

export function buildPermissionMetadata(permissionGroupList = permissionGroups) {
    const parentGroupByPermission = new Map();
    const childGroupByPermission = new Map();

    (permissionGroupList || []).forEach((permissionGroup) => {
        parentGroupByPermission.set(permissionGroup.parentPermission, permissionGroup);
        (permissionGroup.childGroups || []).forEach((childGroup) => {
            (childGroup.childPermissions || []).forEach((childPermission) => {
                childGroupByPermission.set(childPermission, childGroup);
            });
        });
    });

    return {
        parentGroupByPermission,
        childGroupByPermission,
    };
}

const defaultPermissionMetadata = buildPermissionMetadata(permissionGroups);

export function getParentPermissionForValue(permissionValue) {
    const splitValue = splitPermissionValue(permissionValue);
    if (splitValue.isChild) return splitValue.parent;
    return '';
}

export function isChildPermissionValue(permissionValue) {
    return splitPermissionValue(permissionValue).isChild;
}

export function getPermissionDisplayLabel(permissionOption) {
    const baseLabel = permissionOption?.label || permissionOption?.value || '';
    const valueToParse = permissionOption?.value || permissionOption?.label || '';
    const splitValue = splitPermissionValue(valueToParse);
    if (splitValue.isChild) return splitValue.child;
    return baseLabel;
}

function orderPermissionOptions(permissionOptions = []) {
    const options = permissionOptions || [];
    const parentOptions = [];
    const childOptionsByParent = new Map();
    const seenOptionValues = new Set();

    options.forEach((permissionOption) => {
        const optionValue = permissionOption.value;
        if (seenOptionValues.has(optionValue)) return;
        seenOptionValues.add(optionValue);

        const parentPermission = getParentPermissionForValue(optionValue);
        if (parentPermission) {
            if (!childOptionsByParent.has(parentPermission)) childOptionsByParent.set(parentPermission, []);
            childOptionsByParent.get(parentPermission).push(permissionOption);
            return;
        }

        parentOptions.push(permissionOption);
    });

    const orderedOptions = [];
    parentOptions.forEach((parentOption) => {
        const parentValue = parentOption.value;
        orderedOptions.push(parentOption);

        const childOptions = childOptionsByParent.get(parentValue) || [];
        if (!childOptions.length) return;
        // Keep child order exactly as provided by the source options list.
        childOptions.forEach((childOption) => orderedOptions.push(childOption));
    });

    // Strict behavior: children without a visible parent are dropped from the list and are NOT displayed.

    return orderedOptions;
}

export function buildPermissionTree(permissionOptions = []) {
    const orderedOptions = orderPermissionOptions(permissionOptions);
    const groupedPermissions = [];
    let currentParentGroup = null;

    // Ordered list allows a simple single-pass grouping into parent -> children.
    orderedOptions.forEach((permissionOption) => {
        const optionValue = permissionOption.value;
        const isChild = isChildPermissionValue(optionValue);
        const parentPermission = getParentPermissionForValue(optionValue);

        if (!isChild) {
            currentParentGroup = {
                parentOption: permissionOption,
                childOptions: [],
            };
            groupedPermissions.push(currentParentGroup);
            return;
        }

        if (currentParentGroup && currentParentGroup.parentOption?.value === parentPermission) {
            currentParentGroup.childOptions.push(permissionOption);
            return;
        }

        // Strict behavior: orphan child rows are dropped.
    });

    return groupedPermissions;
}

function applyParentToggle(permissionSet, permissionValue, isChecked, permissionMetadata = defaultPermissionMetadata) {
    const permissionGroup = permissionMetadata.parentGroupByPermission.get(permissionValue);
    if (!permissionGroup) return;

    if (isChecked) {
        permissionSet.add(permissionValue);
        (permissionGroup.childGroups || []).forEach((childGroup) => {
            if (childGroup.selectionMode !== 'exclusive') return;
            const selectedCount = (childGroup.childPermissions || []).filter((childPermission) => permissionSet.has(childPermission)).length;
            if (!selectedCount && childGroup.defaultChild) permissionSet.add(childGroup.defaultChild);
        });
        return;
    }

    permissionSet.delete(permissionValue);
    (permissionGroup.childGroups || []).forEach((childGroup) => {
        (childGroup.childPermissions || []).forEach((childPermission) => permissionSet.delete(childPermission));
    });
}

function applyChildToggle(permissionSet, permissionValue, isChecked, permissionMetadata = defaultPermissionMetadata) {
    const parentPermission = getParentPermissionForValue(permissionValue);
    if (!parentPermission || !permissionSet.has(parentPermission)) return;

    const childGroup = permissionMetadata.childGroupByPermission.get(permissionValue)
        || findChildGroup(parentPermission, permissionValue, permissionMetadata);
    const isExclusiveGroup = childGroup?.selectionMode === 'exclusive';

    // In exclusive groups, clicking the selected child should do nothing.
    if (!isChecked && isExclusiveGroup && permissionSet.has(permissionValue)) return;

    if (isChecked) {
        permissionSet.add(permissionValue);
        if (isExclusiveGroup) {
            (childGroup.childPermissions || []).forEach((childPermission) => {
                if (childPermission !== permissionValue) permissionSet.delete(childPermission);
            });
        }
        return;
    }

    permissionSet.delete(permissionValue);
}

export function togglePermissionValue(currentPermissions = [], permissionValue, isChecked, permissionMetadata = defaultPermissionMetadata) {
    const permissionSet = new Set(normalizePermissions(currentPermissions));

    if (permissionMetadata.parentGroupByPermission.has(permissionValue)) {
        applyParentToggle(permissionSet, permissionValue, isChecked, permissionMetadata);
    } else if (isChildPermissionValue(permissionValue)) {
        applyChildToggle(permissionSet, permissionValue, isChecked, permissionMetadata);
    } else if (isChecked) {
        permissionSet.add(permissionValue);
    } else {
        permissionSet.delete(permissionValue);
    }

    return normalizePermissions(Array.from(permissionSet));
}

export function normalizePermissions(permissionValues = []) {
    // IMPORTANT: This function appears in both the client and server code, so any changes should be made in both places.
    const normalizedPermissionSet = new Set(permissionValues || []);

    for (const permissionGroup of permissionGroups) {
        const { parentPermission } = permissionGroup;
        const childPermissions = getAllChildPermissions(permissionGroup);
        const hasParentPermission = normalizedPermissionSet.has(parentPermission);

        // Rule 1: If parent permission is off, every configured child will be removed.
        if (!hasParentPermission) {
            childPermissions.forEach((childPermission) => normalizedPermissionSet.delete(childPermission));
            continue;
        }

        // Rule 2: apply configured child-group constraints (exclusive/default child).
        for (const childGroup of permissionGroup.childGroups || []) {
            const selectedChildren = (childGroup.childPermissions || [])
                .filter((childPermission) => normalizedPermissionSet.has(childPermission));

            if (childGroup.selectionMode === 'exclusive') {
                // If nothing is selected in an exclusive group, force the configured default.
                if (!selectedChildren.length && childGroup.defaultChild) {
                    normalizedPermissionSet.add(childGroup.defaultChild);
                    continue;
                }

                // If multiple are selected in an exclusive group, keep one and drop the rest.
                if (selectedChildren.length > 1) {
                    const keepPermission = (childGroup.childPermissions || [])
                        .find((childPermission) => selectedChildren.includes(childPermission))
                        || selectedChildren[0];
                    selectedChildren.forEach((selectedChild) => {
                        if (selectedChild !== keepPermission) normalizedPermissionSet.delete(selectedChild);
                    });
                }
            }
        }

        // Rule 3: apply the same exclusivity/default behavior to dynamic colon children (Parent:Child)
        // so future additions under a known parent behave safely even if not explicitly configured yet.
        const dynamicChildren = Array.from(normalizedPermissionSet)
            .filter((permissionValue) => {
                const splitValue = splitPermissionValue(permissionValue);
                return splitValue.isChild && splitValue.parent === parentPermission;
            });

        if (dynamicChildren.length > 1) {
            const configuredGroup = (permissionGroup.childGroups || []).find((childGroup) => childGroup.selectionMode === 'exclusive');
            const matchingConfiguredChildren = dynamicChildren.filter((permissionValue) => (
                (configuredGroup?.childPermissions || []).includes(permissionValue)
            ));
            const keepPermission = matchingConfiguredChildren[0] || dynamicChildren[0];
            dynamicChildren.forEach((permissionValue) => {
                if (permissionValue !== keepPermission) normalizedPermissionSet.delete(permissionValue);
            });
        }

        // If a parent has no dynamic child selected, use its configured exclusive default (if present).
        if (!dynamicChildren.length) {
            const configuredGroup = (permissionGroup.childGroups || []).find((childGroup) => childGroup.selectionMode === 'exclusive');
            if (configuredGroup?.defaultChild) normalizedPermissionSet.add(configuredGroup.defaultChild);
        }
    }

    // Rule 4: final orphan sweep. Any Parent:Child entry without its Parent is invalid and removed.
    Array.from(normalizedPermissionSet).forEach((permissionValue) => {
        const splitValue = splitPermissionValue(permissionValue);
        if (splitValue.isChild && !normalizedPermissionSet.has(splitValue.parent)) {
            normalizedPermissionSet.delete(permissionValue);
        }
    });

    return Array.from(normalizedPermissionSet);
}

export function resolveRmaRole(permissionValues = []) {
    const normalizedPermissions = new Set(normalizePermissions(permissionValues));
    if (!normalizedPermissions.has('RMA')) return 'read-only';
    if (normalizedPermissions.has('RMA:Operations')) return 'operations';
    if (normalizedPermissions.has('RMA:Sales')) return 'sales';
    if (normalizedPermissions.has('RMA:Manager')) return 'manager';
    return 'read-only';
}
